// src/app/_components/tag-manager/tag-manager.component.ts
//
// Tag management shared by both tag scopes (tasks: tag migration clean-up §3):
//  - no spaceId  → the current user's own PRIVATE tags (Manage → My private tags, /edit-tags);
//  - a spaceId   → that Space's SPACE tags only (Space admin → Space tags).
// Same list, search, sort, usage counts, create/edit dialog and delete for both; only where the
// data comes from (and goes to) differs, and the server decides the scope from spaceId alone.

import { Component, Input, OnChanges, OnInit, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { ColorPickerModule } from 'primeng/colorpicker';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';

import { DataService, Tag, TagMutationPayload } from '../../_service/data.service';
import { ScopedTagsService } from '../../_service/scoped-tags.service';
import { SnackbarService } from '../../_service/snackbar.service';
import { DEFAULT_TAG_TYPE, TAG_TYPE_OPTIONS, filterPrivateTags, filterSpaceTags } from '../../_util/tag-scope.util';
import {
  COMMON_TAG_COLORS,
  colorValueToString,
  normalizeHexColor,
  readableTextColor,
  slugifyTagName,
} from '../../_util/tag-color.util';

export type TagSortColumn = 'name' | 'color' | 'type' | 'usedIn';

interface TagForm {
  name: string;
  description: string;
  tagType: string;
  slug: string;
  color: string;
  isActive: boolean;
}

@Component({
  selector: 'app-tag-manager',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ColorPickerModule,
    ConfirmDialogModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    ToggleSwitchModule,
    TooltipModule,
  ],
  // Its own instance + <p-confirmDialog>, so it works the same inside any host page.
  providers: [ConfirmationService],
  templateUrl: './tag-manager.component.html',
})
export class TagManagerComponent implements OnInit, OnChanges {
  /** Space whose tags are managed; omitted/null = the current user's private tags. */
  @Input() spaceId: string | null = null;
  /** False shows the list read-only (e.g. a member without write access to the Space). */
  @Input() canEdit = true;

  private readonly dataService = inject(DataService);
  private readonly scopedTags = inject(ScopedTagsService);
  private readonly snackbar = inject(SnackbarService);
  private readonly confirmation = inject(ConfirmationService);

  readonly commonColors = COMMON_TAG_COLORS;
  readonly tagTypeOptions = TAG_TYPE_OPTIONS.map((value) => ({ label: value, value }));

  tags: Tag[] = [];
  /** tag id → number of notes using it; null when it couldn't be determined. */
  usage: Map<string, number> | null = new Map();
  loading = false;
  loadError = '';
  search = '';
  sortColumn: TagSortColumn = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  dialogVisible = false;
  editing: Tag | null = null;
  form: TagForm = this.emptyForm();
  saving = false;
  deletingId: string | null = null;

  get isSpace(): boolean {
    return !!this.spaceId;
  }

  get noun(): string {
    return this.isSpace ? 'Space tag' : 'private tag';
  }

  ngOnInit(): void {
    void this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['spaceId'] && !changes['spaceId'].firstChange) void this.load();
  }

  // ------------------------------------------------------------------ data

  async load(): Promise<void> {
    this.loading = true;
    this.loadError = '';
    try {
      if (this.spaceId) {
        const spaceId = this.spaceId;
        const [tags, notes] = await Promise.all([
          firstValueFrom(this.scopedTags.loadSpaceTags(spaceId)),
          firstValueFrom(this.dataService.getAllNotes()),
        ]);
        this.tags = filterSpaceTags(tags, spaceId);
        // Space tags live in each note's own Tags; this device holds every note of the Space.
        const counts = new Map<string, number>();
        for (const note of notes.data ?? []) {
          if (note.spaceId !== spaceId) continue;
          for (const ref of note.Tags ?? []) {
            const id = typeof ref === 'string' ? ref : ref?._id;
            if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
          }
        }
        this.usage = counts;
      } else {
        const [tagsResp, index] = await Promise.all([
          firstValueFrom(this.dataService.getAllTags()),
          firstValueFrom(this.scopedTags.loadPrivateTagIndex()),
        ]);
        if (tagsResp.status !== 'success') {
          this.loadError = tagsResp.error?.message ?? 'Loading tags failed.';
          this.tags = [];
          return;
        }
        this.tags = filterPrivateTags(tagsResp.data ?? []);
        // Private tags are assigned through the user's own assignment store, not Note.Tags.
        if (index.available) {
          const counts = new Map<string, number>();
          for (const ids of index.byNote.values()) {
            for (const id of ids) counts.set(id, (counts.get(id) ?? 0) + 1);
          }
          this.usage = counts;
        } else {
          this.usage = null;
        }
      }
    } catch (err: any) {
      this.loadError = err?.message ?? 'Loading tags failed.';
    } finally {
      this.loading = false;
    }
  }

  // ------------------------------------------------------------------ list

  get visibleTags(): Tag[] {
    const q = this.search.trim().toLowerCase();
    const matches = this.tags.filter(
      (t) =>
        !q ||
        t.name.toLowerCase().includes(q) ||
        (t.description ?? '').toLowerCase().includes(q) ||
        (t.slug ?? '').toLowerCase().includes(q) ||
        (t.tagType ?? '').toLowerCase().includes(q),
    );
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    const byName = (a: Tag, b: Tag) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    return [...matches].sort((a, b) => {
      let r = 0;
      if (this.sortColumn === 'color') r = normalizeHexColor(a.color).localeCompare(normalizeHexColor(b.color));
      else if (this.sortColumn === 'type') r = (a.tagType ?? '').localeCompare(b.tagType ?? '');
      else if (this.sortColumn === 'usedIn') r = this.usageOf(a._id) - this.usageOf(b._id);
      return (r || byName(a, b)) * dir;
    });
  }

  toggleSort(column: TagSortColumn): void {
    if (this.sortColumn === column) this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
  }

  sortIcon(column: TagSortColumn): string {
    if (this.sortColumn !== column) return 'unfold_more';
    return this.sortDirection === 'asc' ? 'north' : 'south';
  }

  usageOf(tagId: string): number {
    return this.usage?.get(tagId) ?? 0;
  }

  usageLabel(tagId: string): string {
    return this.usage ? String(this.usageOf(tagId)) : '–';
  }

  /** A tag still on notes can't be deleted (deactivate it instead). */
  canDelete(tag: Tag): boolean {
    return this.canEdit && this.usageOf(tag._id) === 0;
  }

  deleteTooltip(tag: Tag): string {
    if (!this.canEdit) return '';
    return this.usageOf(tag._id) > 0 ? 'In use on notes — remove it from them or deactivate it instead' : `Delete ${this.noun}`;
  }

  chipStyle(tag: Tag): Record<string, string> {
    const color = normalizeHexColor(tag.color);
    return color
      ? { 'background-color': color, 'border-color': color, color: readableTextColor(color) }
      : {};
  }

  trackById(_i: number, tag: Tag): string {
    return tag._id;
  }

  // ------------------------------------------------------------------ dialog

  openCreate(): void {
    if (!this.canEdit) return;
    this.editing = null;
    this.form = this.emptyForm();
    this.dialogVisible = true;
  }

  openEdit(tag: Tag): void {
    if (!this.canEdit) return;
    this.editing = tag;
    this.form = {
      name: tag.name,
      description: tag.description ?? '',
      tagType: tag.tagType || DEFAULT_TAG_TYPE,
      slug: tag.slug ?? '',
      color: normalizeHexColor(tag.color),
      isActive: tag.isActive !== false,
    };
    this.dialogVisible = true;
  }

  closeDialog(): void {
    this.dialogVisible = false;
    this.editing = null;
    this.saving = false;
  }

  setColor(value: string | object | null | undefined): void {
    const raw = colorValueToString(value);
    this.form.color = normalizeHexColor(raw) || raw;
  }

  isPresetColor(value: string): boolean {
    return normalizeHexColor(this.form.color) === normalizeHexColor(value);
  }

  /** Name taken by another tag of the same scope/Space (the catalog shown here). */
  get duplicateName(): boolean {
    const name = this.form.name.trim().toLowerCase();
    return !!name && this.tags.some((t) => t._id !== this.editing?._id && t.name.trim().toLowerCase() === name);
  }

  async save(): Promise<void> {
    const name = this.form.name.trim();
    if (!name || this.saving || this.duplicateName) return;
    this.saving = true;
    const color = normalizeHexColor(colorValueToString(this.form.color));
    const payload: TagMutationPayload = {
      name,
      description: this.form.description.trim() || undefined,
      tagType: this.form.tagType || DEFAULT_TAG_TYPE,
      slug: this.form.slug.trim() || slugifyTagName(name),
      color: color || undefined,
      isActive: this.form.isActive,
      ...(this.spaceId ? { spaceId: this.spaceId } : {}),
    };
    try {
      const resp = await firstValueFrom(
        this.editing ? this.dataService.updateTag(this.editing._id, payload) : this.dataService.createTag(payload),
      );
      if (resp.status === 'error' || !resp.data) {
        this.snackbar.showErrorMessage(resp.error?.message ?? 'Saving the tag failed.');
        return;
      }
      const saved: Tag = { ...resp.data, color: normalizeHexColor(resp.data.color ?? color) || undefined };
      this.tags = this.editing
        ? this.tags.map((t) => (t._id === saved._id ? saved : t))
        : [...this.tags, saved];
      if (this.spaceId) this.scopedTags.upsertCachedSpaceTag(saved); // keep pickers/filters current
      this.snackbar.showSuccessMessage(`Tag "${saved.name}" ${this.editing ? 'updated' : 'created'}.`);
      this.closeDialog();
    } finally {
      this.saving = false;
    }
  }

  // ------------------------------------------------------------------ delete

  confirmDelete(tag: Tag): void {
    if (!this.canDelete(tag) || this.deletingId) return;
    const where = this.isSpace ? ' It is removed for every member of this Space.' : '';
    this.confirmation.confirm({
      header: 'Delete tag',
      message: `Delete the ${this.noun} "${tag.name}"? This cannot be undone.${where}`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Delete',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => void this.delete(tag),
    });
  }

  async delete(tag: Tag): Promise<void> {
    if (this.deletingId) return;
    this.deletingId = tag._id;
    try {
      const resp = await firstValueFrom(this.dataService.deleteTag(tag._id, this.spaceId ?? undefined));
      if (resp.status === 'error') {
        this.snackbar.showErrorMessage(resp.error?.message ?? 'Deleting the tag failed.');
        return;
      }
      this.tags = this.tags.filter((t) => t._id !== tag._id);
      if (this.spaceId) this.scopedTags.removeCachedSpaceTag(this.spaceId, tag._id);
      this.snackbar.showSuccessMessage(`Tag "${tag.name}" deleted.`);
    } finally {
      this.deletingId = null;
    }
  }

  private emptyForm(): TagForm {
    return { name: '', description: '', tagType: DEFAULT_TAG_TYPE, slug: '', color: '', isActive: true };
  }
}
